import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Plus, Printer, Trash2, FileText } from "lucide-react";
import { usePacientes } from "@/hooks/usePacientes";
import { useDentistas } from "@/hooks/useDentistas";
import { useHonorarios } from "@/hooks/useHonorarios";
import { useContasReceber } from "@/hooks/useContasReceber";
import {
  PlanoTratamento,
  PlanoTratamentoItem,
  PlanoTratamentoStatus,
  totalPlanoTratamento,
  usePlanosTratamento,
} from "@/hooks/usePlanosTratamento";
import { todayLocalISO } from "@/lib/date-utils";
import { EnviarDocumentoPaciente } from "@/components/documentos/EnviarDocumentoPaciente";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { clinicBrandHeaderHtml, cloudOdontoSignatureHtml } from "@/lib/documentPrint";

const money = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const emptyItem = (): PlanoTratamentoItem => ({
  id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
  procedimento: "",
  dente: "",
  quantidade: 1,
  valor_unitario: 0,
  observacoes: "",
});

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export function PlanosTratamento() {
  const { informacoes } = useInformacoesClinica();
  const { clinicaAtual } = useClinicaDocumento(informacoes);
  const { pacientes } = usePacientes();
  const { dentistas } = useDentistas();
  const { honorarios, fetchHonorarios, activeClinicaId } = useHonorarios();
  const { planos, loading, createPlano, updatePlano, deletePlano } = usePlanosTratamento();
  const { createConta } = useContasReceber();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    paciente_id: "",
    dentista_id: "",
    titulo: "Plano de tratamento",
    status: "Rascunho" as PlanoTratamentoStatus,
    validade: "",
    desconto: 0,
    observacoes: "",
    itens: [emptyItem()],
  });

  useEffect(() => {
    fetchHonorarios();
  }, [activeClinicaId]);

  const total = useMemo(() => totalPlanoTratamento(form), [form]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      paciente_id: "",
      dentista_id: "",
      titulo: "Plano de tratamento",
      status: "Rascunho",
      validade: "",
      desconto: 0,
      observacoes: "",
      itens: [emptyItem()],
    });
  };

  const editPlano = (plano: PlanoTratamento) => {
    setEditingId(plano.id);
    setForm({
      paciente_id: plano.paciente_id,
      dentista_id: plano.dentista_id || "",
      titulo: plano.titulo,
      status: plano.status,
      validade: plano.validade || "",
      desconto: Number(plano.desconto || 0),
      observacoes: plano.observacoes || "",
      itens: plano.itens?.length ? plano.itens : [emptyItem()],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateItem = (id: string, patch: Partial<PlanoTratamentoItem>) => {
    setForm((prev) => ({
      ...prev,
      itens: prev.itens.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  const selectHonorario = (itemId: string, honorarioId: string) => {
    const honorario = honorarios.find((item) => item.id === honorarioId);
    if (!honorario) return;
    updateItem(itemId, {
      procedimento: honorario.procedimento,
      valor_unitario: Number(honorario.valor_particular || 0),
      observacoes: honorario.codigo ? `Código: ${honorario.codigo}` : "",
    });
  };

  const savePlano = async () => {
    const cleanItens = form.itens.filter((item) => item.procedimento.trim());
    if (!form.paciente_id || cleanItens.length === 0) return;

    const payload = {
      ...form,
      dentista_id: form.dentista_id || undefined,
      desconto: Number(form.desconto || 0),
      itens: cleanItens.map((item) => ({
        ...item,
        quantidade: Number(item.quantidade || 1),
        valor_unitario: Number(item.valor_unitario || 0),
      })),
    };

    const saved = editingId ? await updatePlano(editingId, payload) : await createPlano(payload);
    if (saved) resetForm();
  };

  const printPlano = (plano: PlanoTratamento) => {
    const paciente = pacientes.find((item) => item.id === plano.paciente_id);
    const dentista = dentistas.find((item) => item.id === plano.dentista_id);
    const popup = window.open("", "_blank", "width=860,height=720");
    if (!popup) return;

    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${escapeHtml(plano.titulo)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 24px; color: #111827; }
            h1 { margin: 0 0 4px; font-size: 24px; }
            .muted { color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border-bottom: 1px solid #e5e7eb; padding: 9px; text-align: left; }
            th { background: #f3f4f6; font-size: 12px; text-transform: uppercase; color: #4b5563; }
            .total { margin-top: 20px; text-align: right; font-size: 20px; font-weight: bold; }
            .no-print { margin-top: 24px; }
            button { padding: 10px 16px; border: 0; border-radius: 4px; background: #111827; color: white; cursor: pointer; }
            @media print { .no-print { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          ${clinicBrandHeaderHtml(clinicaAtual)}
          <h1>${escapeHtml(plano.titulo)}</h1>
          <div class="muted">Paciente: ${escapeHtml(paciente?.nome || "Paciente")} | Dentista: ${escapeHtml(dentista?.nome || "Não informado")}</div>
          <div class="muted">Status: ${escapeHtml(plano.status)} ${plano.validade ? `| Validade: ${escapeHtml(plano.validade)}` : ""}</div>
          <table>
            <thead><tr><th>Procedimento</th><th>Dente</th><th>Qtd</th><th>Valor</th><th>Total</th></tr></thead>
            <tbody>
              ${(plano.itens || []).map((item) => `
                <tr>
                  <td>${escapeHtml(item.procedimento)}${item.observacoes ? `<br><span class="muted">${escapeHtml(item.observacoes)}</span>` : ""}</td>
                  <td>${escapeHtml(item.dente || "-")}</td>
                  <td>${escapeHtml(item.quantidade)}</td>
                  <td>${money(Number(item.valor_unitario || 0))}</td>
                  <td>${money(Number(item.valor_unitario || 0) * Number(item.quantidade || 0))}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          ${plano.desconto ? `<p class="muted">Desconto: ${money(Number(plano.desconto))}</p>` : ""}
          <div class="total">Total: ${money(totalPlanoTratamento(plano))}</div>
          ${plano.observacoes ? `<p><strong>Observações:</strong><br>${escapeHtml(plano.observacoes)}</p>` : ""}
          ${cloudOdontoSignatureHtml()}
          <div class="no-print"><button onclick="window.print()">Imprimir</button></div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  const gerarCobranca = async (plano: PlanoTratamento) => {
    await createConta({
      paciente_id: plano.paciente_id,
      descricao: `Plano de tratamento: ${plano.titulo}`,
      categoria: "Tratamento",
      valor: totalPlanoTratamento(plano),
      data_vencimento: todayLocalISO(),
      status: "Pendente",
      observacoes: `Gerado a partir do plano ${plano.id}`,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="h-7 w-7" />
            Planos de Tratamento
          </h1>
          <p className="text-muted-foreground">Monte orçamentos por paciente e acompanhe aprovação e execução.</p>
        </div>

        {(pacientes.length === 0 || honorarios.length === 0) && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Para montar um plano completo, cadastre o paciente em Cadastros &gt; Pacientes. A tabela de preços fica em Cadastros &gt; Tabela de Preços; ela ajuda a preencher procedimentos automaticamente, mas voce ainda pode digitar manualmente.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{editingId ? "Editar plano" : "Novo plano"}</CardTitle>
            <CardDescription>Use a tabela de preços para preencher itens rapidamente ou digite manualmente.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Paciente *</Label>
                <Select value={form.paciente_id} onValueChange={(value) => setForm({ ...form, paciente_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
                  <SelectContent>
                    {pacientes.map((paciente) => <SelectItem key={paciente.id} value={paciente.id}>{paciente.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dentista</Label>
                <Select value={form.dentista_id || "none"} onValueChange={(value) => setForm({ ...form, dentista_id: value === "none" ? "" : value })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não informado</SelectItem>
                    {dentistas.map((dentista) => <SelectItem key={dentista.id} value={dentista.id}>{dentista.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value: PlanoTratamentoStatus) => setForm({ ...form, status: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Rascunho", "Enviado", "Aprovado", "Em execução", "Finalizado", "Recusado"].map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Título</Label>
                <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Validade</Label>
                <Input type="date" value={form.validade} onChange={(e) => setForm({ ...form, validade: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Desconto (R$)</Label>
                <MoneyInput value={form.desconto} onValueChange={(desconto) => setForm({ ...form, desconto })} />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              {form.itens.map((item) => (
                <div key={item.id} className="grid gap-3 rounded-md border p-3 lg:grid-cols-[1fr_90px_90px_120px_44px]">
                  <div className="space-y-2">
                    <Label>Procedimento *</Label>
                    <Select value="" onValueChange={(value) => selectHonorario(item.id, value)}>
                      <SelectTrigger><SelectValue placeholder={item.procedimento || "Usar tabela de preços"} /></SelectTrigger>
                      <SelectContent>
                        {honorarios.map((honorario) => (
                          <SelectItem key={honorario.id} value={honorario.id}>
                            {honorario.procedimento} - {money(Number(honorario.valor_particular || 0))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input value={item.procedimento} onChange={(e) => updateItem(item.id, { procedimento: e.target.value })} placeholder="Ou digite o procedimento" />
                  </div>
                  <div className="space-y-2">
                    <Label>Dente</Label>
                    <Input value={item.dente || ""} onChange={(e) => updateItem(item.id, { dente: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Qtd</Label>
                    <Input type="number" min="1" value={item.quantidade} onChange={(e) => updateItem(item.id, { quantidade: Number(e.target.value || 1) })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor</Label>
                    <MoneyInput value={item.valor_unitario} onValueChange={(valor_unitario) => updateItem(item.id, { valor_unitario })} />
                    <p className="text-xs text-muted-foreground">{money(Number(item.valor_unitario || 0) * Number(item.quantidade || 0))}</p>
                  </div>
                  <div className="flex items-end">
                    <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, itens: form.itens.filter((current) => current.id !== item.id) })} disabled={form.itens.length === 1}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={() => setForm({ ...form, itens: [...form.itens, emptyItem()] })}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar procedimento
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>

            <div className="flex items-center justify-between rounded-md bg-muted p-4">
              <div>
                <p className="text-sm text-muted-foreground">Total do plano</p>
                <p className="text-2xl font-bold">{money(total)}</p>
              </div>
              <div className="flex gap-2">
                {editingId && <Button variant="outline" onClick={resetForm}>Cancelar</Button>}
                <Button onClick={savePlano} disabled={!form.paciente_id || form.itens.every((item) => !item.procedimento.trim())}>
                  {editingId ? "Atualizar plano" : "Salvar plano"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Planos cadastrados</CardTitle>
            <CardDescription>{planos.length} plano(s) nesta clínica/unidade</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : planos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum plano cadastrado ainda.</p>
            ) : (
              <div className="space-y-3">
                {planos.map((plano) => {
                  const paciente = pacientes.find((item) => item.id === plano.paciente_id);
                  const dentista = dentistas.find((item) => item.id === plano.dentista_id);
                  return (
                    <div key={plano.id} className="rounded-md border p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold">{plano.titulo}</h3>
                            <Badge>{plano.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {paciente?.nome || "Paciente"} {dentista ? `- Dr(a). ${dentista.nome}` : ""}
                          </p>
                          <p className="mt-1 text-sm">Total: <strong>{money(totalPlanoTratamento(plano))}</strong></p>
                        </div>
                        <div className="flex gap-2">
                          <EnviarDocumentoPaciente entidade="planos_tratamento" documentoId={plano.id} clinicaId={activeClinicaId} titulo={plano.titulo} paciente={paciente} compact />
                          <Button variant="outline" size="sm" onClick={() => editPlano(plano)}>Editar</Button>
                          {plano.status === "Aprovado" && (
                            <Button variant="outline" size="sm" onClick={() => gerarCobranca(plano)}>
                              Gerar cobrança
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => printPlano(plano)} title="Imprimir orçamento">
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deletePlano(plano.id)} title="Excluir">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
